import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  IconButton
} from '@mui/material';
import Phonelink from '@mui/icons-material/Phonelink';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { getMinUsersForTier } from '../../../../utils/tierUsers';
import { useCalculator } from '../../../../contexts/CalculatorContext';
import TierSelector from './TierSelector';
import TierCompareDialog from './TierCompareDialog';

// Features included in each tier
const TIER_FEATURES = {
  basic: [
    { key: 'mobileDesktopApps', default: 'Desktop & mobile apps' },
    { key: 'callRecordingUnlimited', default: 'Unlimited call recording' },
    { key: 'integrations50', default: '50+ ready integrations' },
    { key: 'reportsControlMissed', default: 'Reports & statistics' },
    { key: 'flexibleRouting', default: 'Flexible call routing' },
    { key: 'ivrVoicemail', default: 'IVR, voicemail' },
  ],
  pro: [
    { key: 'allBasicFeatures', default: 'All Basic plan features' },
    { key: 'callTranscription', default: 'Call transcription' },
    { key: 'aiQualityScoring', default: 'AI call quality scoring' },
    { key: 'omnichannelMessengers', default: 'Chat & messengers' },
    { key: 'callbackWidget', default: 'Callback widget' },
    { key: 'advancedReportsSchedule', default: 'Advanced reporting' },
  ],
  corporate: [
    { key: 'allProFeatures', default: 'All Pro plan features' },
    { key: 'advancedConversationAnalytics', default: 'Advanced conversation analytics' },
    { key: 'powerDialer', default: 'Power Dialer' },
    { key: 'missedCallsAutomation', default: 'Missed calls automation' },
    { key: 'personalManager', default: 'Dedicated Customer Success' },
    { key: 'prioritySupport', default: 'Priority support' },
    { key: 'customReportsCrm', default: 'Custom reports & integrations' },
  ],
};

/**
 * BusinessPhoneDemo - New version with Package Tier selection
 * @param {Object} props - Component props
 * @param {Object} props.product - Product data (value, min, max, etc.)
 * @param {Function} props.toggleProduct - Function to toggle product selection
 */
const BusinessPhoneDemo = ({ product, toggleProduct }) => {
  const { t } = useTranslation();
  const {
    businessPhoneSettingsOpen,
    setBusinessPhoneSettingsOpen,
    region,
    veryCustomProductDiscounts,
    showPricesWithoutBonuses
  } = useCalculator();

  // NEW: Package tier state (would normally come from context)
  const [packageTier, setPackageTier] = useState('basic');

  // Compare dialog state
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);

  // Local state for input
  const [usersInput, setUsersInput] = useState(product.value);

  // Get min users based on tier
  const minUsers = getMinUsersForTier(packageTier);

  // Sync local state if the prop values change
  useEffect(() => {
    if (product.value !== usersInput) {
      setUsersInput(product.value);
    }
  }, [product.value]);

  // Auto-adjust users when switching to a tier with a higher minimum
  useEffect(() => {
    if (product.value < minUsers) {
      product.setValue(minUsers);
      setUsersInput(minUsers);
    }
  }, [packageTier]);

  // Open business phone settings dialog
  const handleOpenSettings = () => {
    setBusinessPhoneSettingsOpen(true);
  };

  // Check if there are any custom settings
  const hasAnyCustomSettings = veryCustomProductDiscounts['business_phone'] && veryCustomProductDiscounts['business_phone'] > 0;

  // Get tier display name for header
  const getTierDisplayName = () => {
    const tierNames = {
      basic: 'Basic',
      pro: 'Pro',
      corporate: 'Corporate'
    };
    return tierNames[packageTier] || 'Basic';
  };

  // Get features for current tier
  const currentFeatures = TIER_FEATURES[packageTier] || TIER_FEATURES.basic;

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, mx: { md: -4 }, my: { md: -4 } }}>
      {/* Left part: 70% */}
      <Box sx={{
        width: { xs: '100%', md: '70%' },
        p: { xs: 2, md: 4 },
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Phonelink sx={{ color: '#333', fontSize: 20, mr: 1 }} />
            <Typography sx={{ fontWeight: 500 }}>
              Business Phone {getTierDisplayName()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={handleOpenSettings}
              sx={{
                color: hasAnyCustomSettings ? '#00C853' : '#757575',
                mr: 1,
                '&:hover': { color: '#00C853' }
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => toggleProduct(product.id)}
              sx={{ color: '#333', '&:hover': { color: '#00C853' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Tier Selector */}
        <TierSelector
          selected={packageTier}
          onChange={setPackageTier}
          region={region}
          onCompareClick={() => setCompareDialogOpen(true)}
          showAdvancedTier={false}
        />

        {/* Slider section - pushed to bottom */}
        <Box sx={{ mt: 'auto' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            {t('calculator.businessPhoneUsersLabelStandalone', 'Number of users')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Slider
                value={product.value}
                min={minUsers}
                max={100}
                onChange={(_, v) => {
                  product.setValue(Number(v));
                  setUsersInput(Number(v));
                }}
                sx={{
                  color: '#00C853',
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                    backgroundColor: '#00C853',
                  }
                }}
              />
            </Box>
            <TextField
              value={usersInput}
              onChange={e => setUsersInput(e.target.value)}
              onBlur={() => {
                let numericValue = parseInt(usersInput, 10);
                if (isNaN(numericValue) || numericValue < minUsers) {
                  numericValue = minUsers;
                }
                setUsersInput(numericValue);
                product.setValue(numericValue);
              }}
              type="number"
              variant="outlined"
              size="small"
              sx={{ width: 90 }}
              inputProps={{ inputMode: 'numeric' }}
            />
          </Box>
        </Box>
      </Box>

      {/* Right part: 30% - Features */}
      <Box sx={{
        width: { xs: '100%', md: '30%' },
        bgcolor: '#f8fafc',
        p: { xs: 2, md: 3 },
        borderRadius: { xs: '0 0 12px 12px', md: '0 12px 12px 0' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Typography
          sx={{
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: '0.05em',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            mb: 2
          }}
        >
          {t('calculator.selectedPlanIncludes', 'Selected Plan Includes')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {currentFeatures.map((feature) => (
            <Box
              key={feature.key}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
            >
              <CheckCircleIcon sx={{ color: '#00C853', fontSize: 22 }} />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {t(`calculator.feature.${feature.key}`, feature.default)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* Compare Dialog */}
    <TierCompareDialog
      open={compareDialogOpen}
      onClose={() => setCompareDialogOpen(false)}
      region={region}
      selectedTier={packageTier}
      onTierSelect={(tier) => {
        setPackageTier(tier);
        setCompareDialogOpen(false);
      }}
      showAdvancedTier={false}
      showPricesWithoutBonuses={showPricesWithoutBonuses}
    />
    </>
  );
};

export default React.memo(BusinessPhoneDemo);
